/**
 * Chat-tab starter-prompt generator (PLT-4915, Janne review v8).
 *
 * Replaces the static 4-chip `CHIPS` array that lived in `chat.tsx`
 * through v7. The static cut had two problems:
 *
 *   1. Three of the four chips duplicated what the Positive / Negative
 *      tabs already answer visually ("what drives it", "which products
 *      contribute", positive- and negative-flavoured variants).
 *   2. The wording was valence-agnostic — clicking the negative bar and
 *      landing on the chat tab still led with "What drives the positive
 *      impact?" as the first chip.
 *
 * v8 is a data-aware, valence-adaptive, lightly randomised generator:
 *
 *   - *Slot 1 — Value chain* (always). Juho's note: the new panel no
 *     longer shows the value-chain breakdown the legacy info-boxes did,
 *     so the chat needs a clear entry point into it. Three wordings
 *     rotate via the seed.
 *   - *Slot 2 — Valence-led, product-aware driver* (deterministic). Names
 *     the top contributor product directly so the chip reads as "ask
 *     about THE PRODUCT YOU JUST SAW" rather than a generic "why is it
 *     positive". Falls back to a generic "why is it net-…" wording when
 *     no top product can be named: the contributors data hasn't loaded,
 *     or chat would not explain the top product (PLT-8016).
 *   - *Slot 3 — Methodology* (always). Stable anchor — every panel gets
 *     a "how is this measured" entry. Three wordings rotate.
 *   - *Slot 4 — Wildcard* (peers / levers / something else). Adds a bit
 *     of variation so the panel doesn't feel canned across categories.
 *
 * Determinism: the same (entityId, impactCode) always produces the same
 * picks for slots 1, 3 and 4. Different (entity, impact) pairs produce
 * different mixes, so the surface feels varied across the platform
 * without flickering on re-open. The seed deliberately does NOT include
 * the valence tab, so switching tabs never reshuffles those three.
 *
 * Slot 2 does follow the valence side the user chose (PLT-7788): after
 * clicking the negative bar, or last selecting the Negative tab, it asks
 * about the top negative product, and likewise for positive. With no side
 * chosen (panel opened on About), or no contributors on the chosen side,
 * it falls back to the data-driven pick.
 *
 * Slot 2 names only products chat would explain (PLT-8016). Chat withholds a
 * product breakdown or impact pathway below fixed ¢/$ floors
 * (`impact-magnitude-floors.ts`), so a "why" question about a small side gets
 * declined. A product that fails those floors is treated as absent, and when no
 * side of the panel's impact clears the breakdown floor, slot 2 asks a second
 * methodology question instead of a product one.
 *
 * The richer plan in the Slack thread had SDG / DMA-mapping gates on
 * the wildcard pool; we dropped those for the v8 cut to keep the data
 * dependencies tight (this generator now reads only the payload + the
 * contributors hook). The gates can be added later by extending
 * `ChipContext` with the relevant flags.
 */
import { Contribution, Contributions, FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import {
  isCategorySideTooSmall,
  isImpactLinkTooSmall,
} from '@upright/common/model/output/scores/impact-magnitude-floors';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import type { CategorySideCentsPerDollar } from '../state';

export interface ChipContext {
  /** Friendly company / portfolio name, e.g. "Siemens". */
  entityName: string;
  /** Friendly impact category or dimension, e.g. "physical diseases" / "Environment". */
  impactLabel: string;
  /** Stable id used to seed the deterministic chip-pool picks. */
  entityId: string;
  /** Stable key used as the second half of the seed — the impact code at
   *  impact grain, or `dim:<dimension>` at dimension grain. */
  seedKey: string;
  /**
   * Top positive contributor and its absolute contribution magnitude
   * (the `contribution` field on the drilldown row — already positive
   * for both valences in the backend response). `null` when the
   * contributors data hasn't loaded, errored, the company has no
   * positive contributions for this impact, or chat would decline to
   * explain the top one (see `chatExplainsProductSide`).
   *
   * `code` is the stable product code used to dedupe slot 2 against
   * `topNegative` — a single product can be a company's top positive
   * AND top negative contributor for the same impact (e.g. Siemens'
   * Radiation therapy machines on physical diseases — it both treats
   * disease and exposes patients/staff to radiation). In that case we
   * collapse slot 2 to the dominant-valence single-product wording
   * instead of producing the nonsense "Why does X drive ... more than
   * X drags it down?" contrast chip.
   */
  topPositive: { code: string; name: string; value: number } | null;
  /** Same shape for the largest negative contributor. */
  topNegative: { code: string; name: string; value: number } | null;
  /**
   * The valence side the user chose in the panel: the bar they clicked to
   * open it, or the Positive / Negative tab they selected last. `null` when
   * they have chosen neither, e.g. the panel opened on About.
   */
  selectedValence: 'positive' | 'negative' | null;
  /**
   * The panel's ¢/$ per side, `NetImpactDrillInPayload.positiveCentsPerDollar`
   * and `negativeCentsPerDollar`. They decide the side the generic "net-…"
   * wording names (see `fallbackValence`), and are known before the
   * contributors load.
   */
  positiveCentsPerDollar: number | null;
  negativeCentsPerDollar: number | null;
  /**
   * Whether chat would break at least one side of one covered category
   * down by product (see `anySideBreakdownAvailable`). Read from the
   * payload's ¢/$ figures, so it is known before the contributors load.
   */
  anySideBreakdownAvailable: boolean;
  /**
   * The single impact category the panel covers, or `null` at dimension
   * grain. A product → impact pathway is explained for one category, so
   * the side-panel answer flow (`extra` on the chip) is only offered here.
   */
  impactCode: ImpactCategoryId | null;
}

export interface Chip {
  /** Visible chip text — may include a truncated product name. */
  text: string;
  /** Full prompt sent to the chat panel — uses the untruncated product name. */
  prompt: string;
  /**
   * Hidden-context fields for the chat (`openChatPanel`'s `extra`). Set on
   * the single-product "why" question, whose `intent=explain_product_impact`
   * gives the answer the short side-panel shape described in the chat
   * system prompt (PLT-7886); the same question typed into the chat keeps
   * the full answer.
   */
  extra?: Record<string, string>;
}

/**
 * Cap chip-label length for product names. The chip itself wraps to two
 * lines (`text-wrap: pretty` in `chat.tsx`), but very long names (some
 * pharma / industrial product titles run to 80+ characters) push the
 * chip to three or four lines and break the visual rhythm. We trim the
 * VISIBLE label only — the full name is still passed in the prompt sent
 * to chat so the assistant disambiguates correctly.
 */
const MAX_PRODUCT_NAME_CHIP_LENGTH = 35;

export function truncateProductName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length <= MAX_PRODUCT_NAME_CHIP_LENGTH) return trimmed;
  // Try to use the first segment before a common separator — long
  // product names usually carry the headline noun first, then a
  // qualifier after a comma / dash / colon. Pulling the headline keeps
  // the chip readable.
  const segments = trimmed.split(/[—–\-,;:]/);
  const first = segments[0]?.trim() ?? '';
  if (first.length > 0 && first.length <= MAX_PRODUCT_NAME_CHIP_LENGTH) {
    return first;
  }
  // Fall back to a hard ellipsis cut. -1 to leave room for the ellipsis.
  return `${trimmed.slice(0, MAX_PRODUCT_NAME_CHIP_LENGTH - 1).trimEnd()}…`;
}

/**
 * Tiny string hash → unsigned 32-bit integer. Same FNV-1a constants
 * everyone copies off Wikipedia. We only need a stable, well-spread
 * scalar to seed the per-pool index picks below — no cryptographic
 * properties required.
 */
function hashSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/**
 * Pick one item from `pool` deterministically using `seed + offset`.
 * Each slot calls this with a different `offset` so two adjacent slots
 * picking from same-length pools don't always land on the same index.
 */
function pickFrom<T>(pool: T[], seed: number, offset: number): T {
  // Defensive — caller guarantees non-empty in practice, but better to
  // surface a clear runtime error than to return `undefined` and crash
  // deep in `buildChips`.
  if (pool.length === 0) {
    throw new Error('chat-chips: empty pool passed to pickFrom');
  }
  const idx = (seed + offset) % pool.length;
  return pool[idx];
}

/**
 * Pre-compute the per-call template substitution context. Splitting
 * this out lets each slot read `ctx.lowerImpact` etc. without
 * re-lowercasing in every template builder.
 */
function templateCtx(ctx: ChipContext): { company: string; impact: string } {
  return {
    company: ctx.entityName,
    impact: ctx.impactLabel.toLowerCase(),
  };
}

/* ─── Slot 1 — Value chain (Juho's note) ─────────────────────────────── */

const VALUE_CHAIN_POOL: Array<(t: { company: string; impact: string }) => Chip> = [
  ({ company, impact }) => ({
    text: `Where in ${company}'s value chain does ${impact} arise?`,
    prompt: `Where in ${company}'s value chain does ${impact} arise — upstream suppliers, ${company}'s own operations, or downstream customers? Walk through the main hot spots.`,
  }),
  ({ company, impact }) => ({
    text: `Which parts of ${company}'s value chain drive ${impact}?`,
    prompt: `Which parts of ${company}'s value chain drive ${impact}, and roughly how does the impact split between upstream, own operations, and downstream?`,
  }),
  ({ company, impact }) => ({
    text: `Is ${impact} upstream, in ${company}'s own operations, or downstream?`,
    prompt: `For ${company}'s ${impact} impact, where does it mainly come from — upstream suppliers, ${company}'s own operations, or downstream use of products? Explain the reasoning.`,
  }),
];

/* ─── Slot 2 — Valence-led, product-aware driver ─────────────────────── */

// Single-side wordings name the top contributor and ask just the "why". Asking
// for the mechanism and the contribution calculation in one prompt produced a
// technical wall that trial investors left after (PLT-7519); the chat offers
// the calculation as a follow-up instead. Also the Positive / Negative tabs'
// own "why" button (`ask-why-button.tsx`), so the two surfaces ask the same
// question in the same words.
export function singleSideChip(
  { company, impact }: { company: string; impact: string },
  product: { code: string; name: string },
  valence: 'positive' | 'negative',
  impactCode: ImpactCategoryId | null,
): Chip {
  return {
    text: `Why does ${truncateProductName(product.name)} have a ${valence} impact on ${impact}?`,
    prompt: `Why does ${company}'s ${product.name} have a ${valence} impact on ${impact}?`,
    ...(impactCode !== null
      ? {
          extra: {
            productCode: product.code,
            impactCode,
            valence,
            intent: 'explain_product_impact',
            source: 'ni-drill-in',
          },
        }
      : {}),
  };
}

function valenceDriverChip(ctx: ChipContext): Chip {
  const tctx = templateCtx(ctx);
  const { company, impact } = tctx;

  // The user chose a side: ask about that side's top product alone. The
  // two-product contrast below leads with whichever side is larger, so it
  // would contradict the chosen side whenever the other side is larger.
  // The raw contributors are used here, before the same-product dedupe, so
  // a product that tops both sides is still asked about on the chosen one.
  if (ctx.selectedValence === 'positive' && ctx.topPositive !== null && ctx.topPositive.value > 0) {
    return singleSideChip(tctx, ctx.topPositive, 'positive', ctx.impactCode);
  }
  if (ctx.selectedValence === 'negative' && ctx.topNegative !== null && ctx.topNegative.value > 0) {
    return singleSideChip(tctx, ctx.topNegative, 'negative', ctx.impactCode);
  }

  // Dedupe — a single product can be a company's top positive AND top
  // negative contributor for the same impact (e.g. Siemens' Radiation
  // therapy machines on physical diseases). When that happens we drop
  // the smaller-valence side so slot 2 collapses to the dominant
  // single-product wording ("Why does X have a negative impact on
  // ...?") rather than producing "Why does X drive ... more than X
  // drags it down?" — see Janne's screenshot in the PLT-4915 thread.
  const sameProduct =
    ctx.topPositive !== null && ctx.topNegative !== null && ctx.topPositive.code === ctx.topNegative.code;
  const positiveDominant = sameProduct && ctx.topPositive!.value >= ctx.topNegative!.value;
  const topPos = sameProduct && !positiveDominant ? null : ctx.topPositive;
  const topNeg = sameProduct && positiveDominant ? null : ctx.topNegative;

  const hasPositive = topPos !== null && topPos.value > 0;
  const hasNegative = topNeg !== null && topNeg.value > 0;

  // Both sides present — phrase the chip as the contrast between the
  // dominant contributor and the counter-balancing one.
  if (hasPositive && hasNegative) {
    const posLarger = topPos.value >= topNeg.value;
    if (posLarger) {
      return {
        text: `Why does ${truncateProductName(topPos.name)} drive ${company}'s ${impact} more than ${truncateProductName(
          topNeg.name,
        )} drags it down?`,
        prompt: `For ${company}'s ${impact} impact, why does ${topPos.name} contribute more positively than ${topNeg.name} contributes negatively? Compare the two products' contribution mechanisms.`,
      };
    }
    return {
      text: `Why does ${truncateProductName(topNeg.name)} drag ${company}'s ${impact} down more than ${truncateProductName(
        topPos.name,
      )} lifts it?`,
      prompt: `For ${company}'s ${impact} impact, why does ${topNeg.name} drag the score down more than ${topPos.name} lifts it? Compare the two products' contribution mechanisms.`,
    };
  }

  if (hasPositive) {
    return singleSideChip(tctx, topPos, 'positive', ctx.impactCode);
  }

  if (hasNegative) {
    return singleSideChip(tctx, topNeg, 'negative', ctx.impactCode);
  }

  // Fallback — no top product to name: contributors still loading, or chat
  // would not explain either side's top product.
  const valence = fallbackValence(ctx.positiveCentsPerDollar, ctx.negativeCentsPerDollar, ctx.selectedValence);
  return {
    text: `Why is ${company}'s ${impact} net-${valence}?`,
    prompt: `Why is ${company}'s ${impact} impact net-${valence}? Walk through the main mechanisms and top contributing products.`,
  };
}

/**
 * The side the generic "Why is X's impact net-…?" chip names. The chip states
 * the net direction, so the larger of the panel's two ¢/$ sides decides it.
 * The selected side is used only when the payload has neither figure or the
 * two are equal, and positive after that.
 *
 * Before PLT-8016 this chip showed only while the contributors loaded and
 * defaulted to positive. It now also shows on loaded data when chat would not
 * explain either top product, e.g. a net-negative impact whose negative side is
 * spread over many small products, so the default named the wrong side.
 */
export function fallbackValence(
  positiveCentsPerDollar: number | null,
  negativeCentsPerDollar: number | null,
  selectedValence: 'positive' | 'negative' | null,
): 'positive' | 'negative' {
  // Both sides are magnitudes, as in the panel header's net; `abs` guards a
  // negative side stored with its sign.
  const positive = Math.abs(positiveCentsPerDollar ?? 0);
  const negative = Math.abs(negativeCentsPerDollar ?? 0);
  if (positive !== negative) return positive > negative ? 'positive' : 'negative';
  return selectedValence ?? 'positive';
}

/* ─── Slot 3 — Methodology ───────────────────────────────────────────── */

const METHODOLOGY_POOL: Array<(t: { company: string; impact: string }) => Chip> = [
  ({ impact }) => ({
    text: `How is ${impact} measured?`,
    prompt: `How does Upright measure ${impact} impact? Explain the methodology and what the cents-per-dollar (c/$) figure represents.`,
  }),
  ({ impact }) => ({
    text: `What does the c/$ figure mean for ${impact}?`,
    prompt: `What does the cents-per-dollar (c/$) figure mean for ${impact} impact? How should I read +1.27 c/$ versus -2.5 c/$?`,
  }),
  ({ impact }) => ({
    text: `What sits behind Upright's ${impact} score?`,
    prompt: `What sits behind Upright's ${impact} score — which data sources, models, and assumptions feed it?`,
  }),
];

/* ─── Slot 4 — Wildcard (peers / levers / outliers) ──────────────────── */

const WILDCARD_POOL: Array<(t: { company: string; impact: string }) => Chip> = [
  ({ company, impact }) => ({
    text: `How does ${company} compare to peers on ${impact}?`,
    prompt: `How does ${company} compare to industry peers on ${impact} impact — both in size and in which products drive it?`,
  }),
  ({ company, impact }) => ({
    text: `What would move ${company}'s ${impact}?`,
    prompt: `What would meaningfully move ${company}'s ${impact} impact — changes in product mix, operations, or value chain? Be concrete about the levers.`,
  }),
  ({ company, impact }) => ({
    text: `What's the broader context for ${company}'s ${impact}?`,
    prompt: `Beyond the headline number, what's the broader context for ${company}'s ${impact} impact — long-term trends, regulatory exposure, or strategic implications?`,
  }),
];

/* ─── Public entry point ─────────────────────────────────────────────── */

/**
 * Build the 4 starter chips for a given drill-in panel. Pure function —
 * no side effects, no React. Always returns exactly 4 chips in the
 * fixed slot order [value-chain, driver, methodology, wildcard] so the
 * UI keeps a stable visual layout.
 */
export function buildChips(ctx: ChipContext): Chip[] {
  const tctx = templateCtx(ctx);
  // Seed off (entity, target) only, NOT the selected valence, so slots 1,
  // 3 and 4 stay put when the user switches between Positive and Negative.
  const seed = hashSeed(`${ctx.entityId}|${ctx.seedKey}`);
  return [
    pickFrom(VALUE_CHAIN_POOL, seed, 0)(tctx),
    // With every side below chat's breakdown floor, any product or
    // mechanism-by-product question would be declined (PLT-8016). The
    // other methodology wording takes the slot: offset 2 on a 3-item pool
    // never lands on slot 3's offset-1 pick.
    ctx.anySideBreakdownAvailable ? valenceDriverChip(ctx) : pickFrom(METHODOLOGY_POOL, seed, 2)(tctx),
    pickFrom(METHODOLOGY_POOL, seed, 1)(tctx),
    pickFrom(WILDCARD_POOL, seed, 2)(tctx),
  ];
}

/* ─── Helpers exported for `chat.tsx` ────────────────────────────────── */

/**
 * Pull the top contributor for each valence out of the
 * `useCompanyDrilldownContributions` payload. Returns
 * `{ topPositive: null, topNegative: null }` when the data is
 * unavailable so the caller can render the fallback chip without
 * branching on loading / error / missing-product-name states.
 *
 * A top contributor chat would not explain is returned as `null` too.
 * At impact grain every other product on that side has a smaller share,
 * so none of them would pass either. At dimension grain a smaller product
 * can pass in a category where the top one fails; it is not looked for,
 * so slot 2 falls back and offers one question too few rather than one
 * chat declines.
 */
export function extractTopContributors(
  contributions: Contributions | undefined,
  impactCodes: ImpactCategoryId[],
  productNamesById: Record<string, string>,
  sideCentsPerDollar: CategorySideCentsPerDollar,
): {
  topPositive: { code: string; name: string; value: number } | null;
  topNegative: { code: string; name: string; value: number } | null;
} {
  const explained = (valence: FinalScoreValence) => {
    const top = topContributor(contributions, impactCodes, valence, productNamesById);
    if (!contributions || top === null) return null;
    return chatExplainsProductSide(contributions, impactCodes, sideCentsPerDollar, top.code, valence) ? top : null;
  };
  return {
    topPositive: explained(Valence.POSITIVE),
    topNegative: explained(Valence.NEGATIVE),
  };
}

/**
 * Whether chat would explain why `productCode` drives the `valence` side
 * of the panel's impact (PLT-8016). True when, for at least one covered
 * category, the side clears the breakdown floor `get_contributing_products`
 * applies and the product's slice of it clears the link floor
 * `get_product_impact_reasoning` applies to a named company.
 *
 * The slice is the drilldown share times the side's ¢/$. The share is
 * `ratio × relScore / relTotal` and the ¢/$ conversion is linear, so the
 * product is the revenue-weighted ¢/$ the reasoning tool measures.
 */
export function chatExplainsProductSide(
  contributions: Contributions,
  impactCodes: ImpactCategoryId[],
  sideCentsPerDollar: CategorySideCentsPerDollar,
  productCode: string,
  valence: FinalScoreValence,
): boolean {
  return impactCodes.some(impactCode => {
    const side = sideCentsPerDollar[impactCode]?.[valence] ?? null;
    if (side === null || isCategorySideTooSmall(side)) return false;
    const contribution: Contribution | undefined = contributions[impactCode]?.[valence]?.[productCode];
    const share = contribution?.contribution ?? 0;
    return !isImpactLinkTooSmall(share * side, 'company-weighted');
  });
}

/**
 * Whether chat would break at least one side of one covered category down
 * by product. When none clears the floor, chat declines every product
 * question about this impact, whichever product it names.
 */
export function anySideBreakdownAvailable(
  impactCodes: ImpactCategoryId[],
  sideCentsPerDollar: CategorySideCentsPerDollar,
): boolean {
  const valences: FinalScoreValence[] = [Valence.POSITIVE, Valence.NEGATIVE];
  return impactCodes.some(impactCode =>
    valences.some(valence => {
      const side = sideCentsPerDollar[impactCode]?.[valence] ?? null;
      return side !== null && !isCategorySideTooSmall(side);
    }),
  );
}

function topContributor(
  contributions: Contributions | undefined,
  impactCodes: ImpactCategoryId[],
  valence: FinalScoreValence,
  productNamesById: Record<string, string>,
): { code: string; name: string; value: number } | null {
  if (!contributions) return null;
  // Sum each product across all impact codes the panel covers (one code
  // at impact grain, the dimension's member categories at dimension
  // grain) before picking the single largest contributor (PLT-4940).
  const valueByProduct = new Map<string, number>();
  for (const impactCode of impactCodes) {
    const byProduct = contributions[impactCode]?.[valence];
    if (!byProduct) continue;
    for (const code of Object.keys(byProduct)) {
      const c: Contribution = byProduct[code];
      const v = c.contribution || 0;
      if (v > 0) valueByProduct.set(code, (valueByProduct.get(code) ?? 0) + v);
    }
  }
  let bestCode: string | null = null;
  let bestValue = 0;
  for (const [code, v] of valueByProduct) {
    if (v > bestValue) {
      bestValue = v;
      bestCode = code;
    }
  }
  if (bestCode === null || bestValue <= 0) return null;
  const name = productNamesById[bestCode];
  // Fall back to the bare product code when we can't resolve a name —
  // it's still more specific than the generic "why is it negative?"
  // fallback chip.
  return { code: bestCode, name: name && name.trim().length > 0 ? name : bestCode, value: bestValue };
}
